import {Button, Checkbox, Divider, Popover, Select, Spin, Tooltip,} from 'antd';
import React, {useEffect, useRef, useState} from 'react';
import OperationMenu from '../OperationMenu';
import {CheckOperations, ComparisonOperations} from './Operations';
import {AppActions, useAppContext} from '../../context/AppContext';
import GrayButton from '../../components/GrayButton';
import SelectPlan from '../SelectPlan';
import {b} from 'plotly.js/src/traces/carpet/attributes';
import HecRasHelper from '../../helpers/HecRasHelper';
import ProjectInfo from '../ProjectInfo';
import {
    BarChartOutlined,
    CaretRightOutlined,
    DotChartOutlined,
    ForwardOutlined,
    GlobalOutlined,
    PicLeftOutlined,
    TableOutlined,
    WarningOutlined,
} from '@ant-design/icons';
import ApiService from '../../services/ApiService';
import {useUser} from '../../context/UserContext';
import {useHistory} from 'react-router-dom';
import {filterRunOperations, getPermissionSet} from '../../helpers/PermissionHelper';
import PopoverScrollContent from '../../components/PopoverScrollContent';
import SelectionInfo from "./SelectionInfo";
import {DEFAULT_PROJECTION, getProjectionOptions, loadProjectionText} from '../../helpers/ProjectionHelper';
import ScoreAnalysisModal from './ScoreAnalysisModal';
import {computeSeverity, isErrorSeverity} from './operationStatus';
import SummaryTableModal from './SummaryTableModal';
import HtmlReportButton from './HtmlReport';
import MessageCountsModal from './MessageCountsModal';

export const OPS_STATUS = {
    NOT_CHECK: 0,
    CHECKING: 1,
    COMPLETED: 2,
    ERROR: 3,
}

export default function RunOperations({runType}) {
    const { appContext, dispatchProject, selectedHistoryId, selection: selectionRef, getSelectedHistory, resolveSelection, resolveHistorySelection } =
        useAppContext();
    // Components expect the resolved shape (selection.base.project.<heavy>).
    // The store only holds id refs; we resolve on read so the rest of the
    // file keeps reading `selection.base.plan?.geometry?.text` etc. unchanged.
    const selection = resolveSelection(selectionRef) || {base: {}, compare: {}};
    const {settings, user} = useUser();
    const history = useHistory();
    const [assoc, setAssoc] = useState(true);
    const [running, setRunning] = useState({
        text: '',
        running: false,
    });
    const [operations, setOperations] = useState([]);
    const isBatchingRef = useRef(false);
    const [summaryModalVisible, setSummaryModalVisible] = useState(false);
    const [scoreAnalysisModalVisible, setScoreAnalysisModalVisible] = useState(false);
    const [messageCountsModalVisible, setMessageCountsModalVisible] = useState(false);
    // Default projection for projects without .prj file
    const [defaultProjectionKey, setDefaultProjectionKey] = useState(
        user?.settings?.defaults?.spatial_projection || DEFAULT_PROJECTION
    );
    const [defaultProjectionText, setDefaultProjectionText] = useState(null);

    // Load default projection text when key changes
    useEffect(() => {
        const loadDefaultProjection = async () => {
            const text = await loadProjectionText(defaultProjectionKey);
            setDefaultProjectionText(text);
        };
        loadDefaultProjection();
    }, [defaultProjectionKey]);

    const getSelectionId = (selection) => {
        if (runType === 'Check') {
            return `${selection?.base?.plan?.id}_${selection?.base?.river?.key}`;
        } else {
            return `${selection?.base?.plan?.id}_${selection?.base?.river?.key}_${selection?.compare?.plan?.id}_${selection?.compare?.river?.key}`;
        }
    };

    const getInputData = ({_selection}) => {
        // Use project projection if available, otherwise use default projection
        const hasProjectProjection = !!_selection?.project?.projection?.text;
        const projectionToUse = hasProjectProjection 
            ? _selection?.project?.projection 
            : (defaultProjectionText ? { text: defaultProjectionText, isDefault: true } : null);

        return {
            project: _selection.project.information,
            projection: projectionToUse,
            geometry: _selection.plan.geometry,
            flow: _selection.plan.flow,
            output: _selection.plan.mappedProfiles ?? [],
            plan: _selection.plan.plan,
            type: _selection.plan.type,
            profileNames: _selection.plan.mapProfiles.map((x) => {
                if (!x.targetName) x.targetName = x.target;

                return x;
            }),
            settings: user.settings,
            projectHash: _selection.projectHash,
            river: _selection.river?.river || null,
            reach: _selection.river?.reach || null,
            survey_geojson: _selection?.survey || null
        };
    };

    const batchRun = async () => {
        // Batch sirasinda selection degistikce useEffect'in operations'i sifirlamasini engelle.
        isBatchingRef.current = true;
        try {

        let histories = [...appContext?.history];

        let total = 0;
        const planSet = new Set();
        const riverSet = new Set();
        for (const project of appContext?.projects || []) {
            for (const plan of project?.projectFiles || []) {
                if (plan?.rivers?.length > 0) {
                    total += plan.rivers.length;
                    planSet.add(plan?.id);
                    for (const river of plan.rivers) {
                        riverSet.add(river?.river);
                    }
                }
            }
        }
        const planCount = planSet.size;
        const riverCount = riverSet.size;

        // Kullanicinin secim niyetini key bazli sakla. Uc durum var:
        //   'on'    -> panelde gorunurdu, isaretli birakildi
        //   'off'   -> panelde gorunurdu, kullanici uncheck etti
        //   (yok)   -> ilk planda system-disabled idi, niyet belirtilmedi
        // Disabled satirlari haritaya almiyoruz: capable bir planda otomatik
        // calistirilsinlar (kullanici "select all"da birakmis kabul edilir).
        // Hicbir intent yoksa (operations bos) eski "hepsini sec" davranisina duseriz.
        const userIntentByKey = {};
        let hasUserIntent = false;
        for (const op of operations || []) {
            for (const child of op?.children || []) {
                if (child?.key == null) continue;
                if (child.disabled) continue;
                userIntentByKey[child.key] = child.enable ? 'on' : 'off';
                hasUserIntent = true;
            }
        }

        let current = 0;
        for (const project of appContext?.projects) {
            for (const plan of project?.projectFiles) {
                if (plan?.rivers?.length > 0) {
                    for (const river of plan?.rivers) {
                        current++;
                        // Store: tiny id-only ref. Local logic: resolved.
                        const _selectionRef = {
                            base: {
                                projectId: project?.id,
                                planId: plan?.id,
                                river,
                            },
                            compare: {},
                        };
                        const _selection = resolveSelection(_selectionRef);
                        dispatchProject({
                            type: AppActions.SET_PROJECT_SELECTION,
                            data: {selection: _selectionRef},
                        });
                        const _ops = [
                            ...getOperations(_selection, getSelectionId(_selection), true),
                        ];

                        if (hasUserIntent) {
                            // Plan-bazli capability'den gelen `disabled` korunur.
                            // Capable olanlar icin: 'off' niyeti varsa kapat,
                            // 'on' veya niyet yoksa (ilk planda disabled idi) ac.
                            for (const op of _ops) {
                                for (const child of op?.children || []) {
                                    if (child?.disabled) continue;
                                    child.enable = userIntentByKey[child.key] !== 'off';
                                }
                            }
                        }
                        const planName = plan?.plan?.fileName || plan?.plan?.shortIdentifier || plan?.plan?.title || '';
                        const riverName = river?.river || river?.name || '';
                        const data = await run(
                            histories,
                            _selectionRef,
                            _ops,
                            (ops) => {
                                setOperations([...ops]);
                            },
                            {
                                current,
                                total,
                                remaining: total - current,
                                planName,
                                riverName,
                                planCount,
                                riverCount,
                            }
                        );

                        dispatchProject({
                            type: AppActions.SET_SELECTED_HISTORY_ID,
                            data: {id: data.id}
                        })
                    }
                }
            }
        }

        dispatchProject({
            type: AppActions.SET_HISTORY,
            data: {
                history: histories,
            },
        });

        } finally {
            isBatchingRef.current = false;
        }
    };

    const run = async (
        histories,
        selectionInput,
        _operations,
        onUpdatedOperations,
        batchInfo
    ) => {
        // Callers (single-run button, batchRun loop) may pass either the
        // resolved selection or the id-ref form. Normalise to both forms so
        // run-time logic can read heavy bodies but the history persists only
        // the small id-ref.
        const isResolved = !!(selectionInput?.base?.project || selectionInput?.compare?.project)
        const selectionRef = isResolved
            ? {
                base: selectionInput.base ? {
                    projectId: selectionInput.base.projectId || selectionInput.base.project?.id || selectionInput.base.project?.information?.id,
                    planId: selectionInput.base.planId || selectionInput.base.plan?.id,
                    river: selectionInput.base.river,
                    survey: selectionInput.base.survey,
                } : {},
                compare: selectionInput.compare ? {
                    projectId: selectionInput.compare.projectId || selectionInput.compare.project?.id || selectionInput.compare.project?.information?.id,
                    planId: selectionInput.compare.planId || selectionInput.compare.plan?.id,
                    river: selectionInput.compare.river,
                    survey: selectionInput.compare.survey,
                } : {},
            }
            : selectionInput
        const selection = isResolved ? selectionInput : resolveSelection(selectionInput)

        let parsed = {};
        setRunning({
            running: true,
            text: 'Project Preparing...',
            batchInfo,
        });

        let intersection_child_key = [
            'ST1',
            'ST2',
            'XY2',
            'XY4',
            'XY5',
            'XY6',
            'XY7',
        ];
        let calc_intersection = false;
        let include_survey_data = false

        for (const operation of _operations) {
            for (const child of operation.children) {
                if (
                    intersection_child_key.includes(child.key_text) &&
                    child?.enable
                ) {
                    calc_intersection = true;
                }

                if (child.type === 'survey' && child.enable) {
                    include_survey_data = true
                }
            }
            if (calc_intersection) break;
        }

        let interval = null;
        let timer = 0;
        interval = setInterval(() => {
            timer++;
            if (timer <= 3) {
                setRunning({
                    running: true,
                    text: `Project Preparing... (${timer} s)`,
                    batchInfo,
                });
            } else {
                setRunning({
                    running: true,
                    text: `Spatial pre-calculations in progress... (${timer} s)`,
                    batchInfo,
                });
            }
        }, 1000);

        if (runType === 'Check') {
            const inputData = getInputData({_selection: selection.base})

            const res = await ApiService.RasCheck.ParseInternal({
                ...inputData,
                calc_intersection,
            });
            if (res.success) parsed = JSON.parse(res.data);
        } else {
            const data = {
                selected: getInputData({_selection: selection.base}),
                target: getInputData({_selection: selection.compare}),
                settings: user.settings,
                isAssoc: assoc,
                calc_intersection,
            };
            const res = await ApiService.RasCheck.ParseCompare(data);
            if (res.success) parsed = JSON.parse(res.data);
        }

        clearInterval(interval);
        setRunning({
            running: true,
            text: `Spatial pre-calculations are completed in ${timer} seconds.`,
            batchInfo,
        });

        let req = ApiService.RasCheck.InternalCheck;
        let input = {
            key: parsed.key,
            projectHash: selection.base.plan.projectHash,
        };
        if (runType === 'Comparison') {
            req = ApiService.RasCheck.CompareCheck;
            input = {
                key: parsed.key,
                baseHash: selection.base.plan.projectHash,
                compareHash: selection.compare.plan.projectHash,
            };
        }

        for (const operation of _operations) {
            for (const child of operation.children) {
                try {
                    if (!child.enable) continue;

                    // Informational items (e.g. the Overview group) render from
                    // the history-level summary/calculation_tolerances produced by
                    // the pre-calc step — they have no per-check backend request.
                    if (child.informational || !child.request) {
                        // Overview messages are real findings when there is a diff,
                        // computed by the backend (no client-side recompute):
                        //  - internal OV4: plan tolerance vs user default
                        //  - compare C-OV1/2/3: base vs comparison plan
                        // A non-empty result group makes computeSeverity flag it
                        // ERROR (red); otherwise it stays INFO (blue).
                        let result = [];
                        const ovDiff = parsed?.summary?.overview_diff || {};
                        const COMPARE_OV = {
                            'C-OV1': {differs: ovDiff.location, error_id: 'C-OV 01'},
                            'C-OV2': {differs: ovDiff.geometry, error_id: 'C-OV 02'},
                            'C-OV3': {differs: ovDiff.statistics, error_id: 'C-OV 03'},
                        };
                        if (child.key === 'OV4' && parsed?.calculation_tolerances?.differs_from_user_default)
                            // Plan tolerans degerleri + User Default'u sonuca gom; PS00
                            // fix'i bunlari okuyup duzeltilecek alanlari listeler.
                            result = [{error_id: 'OV 04', data: [{
                                tolerances: parsed.calculation_tolerances,
                                user_default: user?.settings?.calculation_tolerances || {},
                            }]}];
                        else if (COMPARE_OV[child.key] && COMPARE_OV[child.key].differs)
                            result = [{error_id: COMPARE_OV[child.key].error_id, data: [{}]}];

                        child.status = OPS_STATUS.COMPLETED;
                        child.time = 0;
                        child.data = {result};
                        child.originalData = {result};
                        child.enable = false;
                        child.severity = computeSeverity(child);
                        child.hasError = isErrorSeverity(child.severity);
                        onUpdatedOperations &&
                        onUpdatedOperations([..._operations]);
                        continue;
                    }

                    child.status = OPS_STATUS.CHECKING;
                    onUpdatedOperations &&
                    onUpdatedOperations([..._operations]);
                    let time = new Date();
                    const res = await req({
                        ...input,
                        target: child.request,
                    });

                    let time2 = new Date();
                    const sec = (time2.getTime() - time.getTime()) / 1000;
                    if (res.success) {
                        child.status = OPS_STATUS.COMPLETED;
                        child.time = sec;
                        child.data = JSON.parse(res.data);
                        child.originalData = JSON.parse(res.data);
                        child.enable = false;
                        child.severity = computeSeverity(child);
                        child.hasError = isErrorSeverity(child.severity);
                    } else {
                        child.status = OPS_STATUS.ERROR;
                        child.time = sec;
                    }
                    onUpdatedOperations &&
                    onUpdatedOperations([..._operations]);
                } catch (exp) {
                    child.status = OPS_STATUS.ERROR;
                    onUpdatedOperations &&
                    onUpdatedOperations([..._operations]);
                    console.error('Error: ', exp);
                }
            }
        }

        let _wsel = {};
        if (runType === 'Comparison') {
            const findResult = (_operations, key) => {
                let res = {};
                _operations.forEach((op) => {
                    const x = op.children.find((x) => x.key === key);
                    if (x) {
                        res = x;
                    }
                });
                return res;
            };

            try {
                const data = {
                    C11: findResult(_operations, 'C11') || [-1, -1], // C.11
                    C1: findResult(_operations, 'C1') || {}, // C.1
                    C2: findResult(_operations, 'C2') || {}, //C.2
                    C3: findResult(_operations, 'C3') || {}, //C.3
                    C4: findResult(_operations, 'C4') || {}, // C.4
                    C5: findResult(_operations, 'C5') || {}, // C.5
                    C6: findResult(_operations, 'C6') || {}, // C.6
                    C7: findResult(_operations, 'C7') || {}, // C.7
                    C12: findResult(_operations, 'C12') || {}, // C.12
                    C13: findResult(_operations, 'C13') || {}, // C.13
                    C15: findResult(_operations, 'C15') || {}, // C.15
                    C20: findResult(_operations, 'C20') || {}, // C.12
                    C21: findResult(_operations, 'C21') || {}, // C.13
                };

                _wsel = HecRasHelper.calcWSEL({
                    ...data,
                    minCS: Math.min(
                        ...(parsed?.summary?.compare?.cs_list || []).map(
                            (x) => x.value
                        )
                    ),
                    maxCS: Math.max(
                        ...(parsed?.summary?.compare?.cs_list || []).map(
                            (x) => x.value
                        )
                    ),
                    csList: parsed?.summary?.compare?.cs_list || [],
                });
            } catch (exp) {
                console.log(exp);
            }

            setRunning({
                running: false,
                text: '',
            });
        }

        const del = await ApiService.RasCheck.RunComplete(parsed.key);
        setOperations([..._operations]);

        const id = getSelectionId(selection);

        const index = histories.findIndex((x) => x.id === id);
        if (index > -1) {
            histories[index] = {
                ...histories[index],
                date: new Date(),
                operations: [..._operations],
                summary: parsed.summary,
                calculation_tolerances: parsed.calculation_tolerances,
                mapBase: parsed.map_base,
                mapCompare: parsed.map_cmp,
                associate: parsed.associate,
                wsel: _wsel,
            };
        } else {
            histories.push({
                id,
                type: runType,
                // Persist id-only ref so we don't snapshot the entire project
                // (geometry/output/shapeFiles) into every history.
                selection: {
                    base: {...(selectionRef?.base || {})},
                    compare: {...(selectionRef?.compare || {})},
                },
                date: new Date(),
                operations: [..._operations],
                summary: parsed.summary,
                calculation_tolerances: parsed.calculation_tolerances,
                mapBase: parsed.map_base,
                mapCompare: parsed.map_cmp,
                wsel: _wsel,
            });
        }

        setRunning({
            running: false,
            text: '',
        });

        return {histories, id};
    };

    const getOperations = (selection, historyId, selected) => {
        const history = appContext?.history?.find((x) => x.id === historyId);

        let _operations = [];
        if (history)
            _operations = history?.operations || [];
        else if (runType === 'Check') {
            _operations = JSON.parse(JSON.stringify(CheckOperations));
        } else {
            _operations = JSON.parse(JSON.stringify(ComparisonOperations));
        }

        const permSet = getPermissionSet(user);
        _operations = filterRunOperations(runType, _operations, permSet);

        return _operations.map((op) => {
            op.disabled = false;
            op.children.forEach((child) => {
                child.enable = selected;
                child.disabled = false;

                // Helper to check if child has a specific type (supports both string and array)
                const hasType = (typeName) => {
                    if (Array.isArray(child.type)) {
                        return child.type.includes(typeName);
                    }
                    return child.type === typeName;
                };

                if (
                    (hasType('output') || hasType('floadway') || hasType('yr100') || hasType('library')) &&
                    !hasOutput()
                ) {
                    child.enable = false;
                    child.disabled = true;
                }
                if (hasType('floadway') && !hasFloadway(selection)) {
                    child.enable = false;
                    child.disabled = true;
                    // op.disabled = true
                } else if (hasType('yr100') && !has100yr(selection)) {
                    child.enable = false;
                    child.disabled = true;
                    // op.disabled = true
                } else if (hasType('library') && !isLibraryPlan(selection)) {
                    child.enable = false;
                    child.disabled = true;
                    op.disabled = true;
                } else if (hasType('structure') && !hasStructure(selection)) {
                    child.enable = false;
                    child.disabled = true;
                    op.disabled = true;
                } else if (hasType('schematic') && !hasGeoReference(selection)) {
                    child.enable = false;
                    child.disabled = true;
                    op.disabled = true;
                } else if (hasType('survey') && !hasSurvey(selection)) {
                    child.enable = false;
                    child.disabled = true;
                    op.disabled = true;
                }
            });

            if (op.children.length > 0 && op.children.every((c) => c.disabled)) {
                op.disabled = true;
            }

            return op;
        });
    };

    const isLibraryPlan = (selection) => {
        if (runType === 'Check') {
            return HecRasHelper.isLibraryPlan(selection.base.plan);
        } else {
            return (
                HecRasHelper.isLibraryPlan(selection.base.plan) &&
                HecRasHelper.isLibraryPlan(selection.compare.plan)
            );
        }
    };

    const hasGeoReference = (selection) => {
        if (runType === 'Check') {
            return HecRasHelper.hasGeoReferenced(
                selection.base.plan?.geometry?.text
            );
        } else {
            return (
                HecRasHelper.hasGeoReferenced(
                    selection.base.plan?.geometry?.text
                ) &&
                HecRasHelper.hasGeoReferenced(
                    selection.compare.plan?.geometry?.text
                )
            );
        }
    };

    const hasSurvey = (selection) => {
        if (runType === 'Check') {
            const survey = selection?.base?.project?.shapeFiles?.find(x => x.hasSurveyData)
            if (survey)
                return true;
        } else {
            const survey_base = selection?.base?.project?.shapeFiles?.find(x => x.hasSurveyData)
            const survey_cmp = selection?.compare?.project?.shapeFiles?.find(x => x.hasSurveyData)
            if (survey_base && survey_cmp)
                return true
        }

        return false
    };

    const hasStructure = (selection) => {
        if (runType === 'Check') {
            return (
                HecRasHelper.getStructureList(
                    selection.base.plan?.geometry?.text,
                    selection.base?.river?.river,
                    selection.base?.river?.reach
                )?.length > 0
            );
        } else {
            return (
                HecRasHelper.getStructureList(
                    selection.base.plan?.geometry?.text,
                    selection.base?.river?.river,
                    selection.base?.river?.reach
                ) &&
                HecRasHelper.getStructureList(
                    selection.compare.plan?.geometry?.text,
                    selection.compare?.river?.river,
                    selection.compare?.river?.reach
                )
            );
        }
    };

    const hasFloadway = (selection) => {
        if (runType === 'Check') {
            return selection?.base?.plan?.mapProfiles?.find(
                (x) => x.target === 'FW'
            );
        } else {
            return (
                selection?.base?.plan?.mapProfiles?.find(
                    (x) => x.target === 'FW'
                ) &&
                selection?.compare?.plan?.mapProfiles?.find(
                    (x) => x.target === 'FW'
                )
            );
        }
    };

    const hasOutput = () => {
        if (runType === 'Check') {
            return (
                selection?.base?.project?.information?.hasOutputFile === 'true' &&
                selection?.base?.plan?.isFindOutput !== 'false'
            );
        } else {
            return (
                selection?.base?.project?.information?.hasOutputFile === 'true' &&
                selection?.base?.plan?.isFindOutput !== 'false' &&
                selection?.compare?.project?.information?.hasOutputFile === 'true' &&
                selection?.compare?.plan?.isFindOutput !== 'false'
            );
        }
    };

    const has100yr = (selection) => {
        if (runType === 'Check') {
            return selection?.base?.plan?.mapProfiles?.find(
                (x) => x.target === 'YR100'
            );
        } else {
            return (
                selection?.base?.plan?.mapProfiles?.find(
                    (x) => x.target === 'YR100'
                ) &&
                selection?.compare?.plan?.mapProfiles?.find(
                    (x) => x.target === 'YR100'
                )
            );
        }
    };

    const isOperationSelectedAll = () => {
        for (const operation of operations) {
            for (const child of operation?.children || []) {
                if (!child.enable && !child.disabled) return false;
            }
        }

        return true;
    };

    const findHistory = () => {
        return appContext?.history?.find(
            (x) => x.id === getSelectionId(selection)
        );
    };

    const getFirstErrorMessage = () => {
        const historyData = findHistory();
        if (historyData) {
            for (const operation of historyData.operations) {
                for (const child of operation.children) {
                    if (child.data && child?.data?.result?.length > 0) {
                        return child;
                    }
                }
            }
        }
    };

    useEffect(() => {
        // Batch sirasinda her plan/river degisiminde panel state'i sifirlama —
        // kullanicinin baslangictaki secimini koruyalim.
        if (isBatchingRef.current) return;
        if (selection.base.plan || selection.compare.plan) {
            setOperations([...getOperations(selection, getSelectionId(selection), true)]);
        }
    }, [
        selection.base.plan?.id,
        selection?.base?.river?.key,
        selection.compare.plan?.id,
        selection?.compare?.river?.key,
        selection?.base?.project?.information?.hasOutputFile,
        selection?.base?.plan?.isFindOutput,
        selection?.compare?.project?.information?.hasOutputFile,
        selection?.compare?.plan?.isFindOutput,
        runType,
    ]);

    return (
        <div>
            <Button
                disabled={running.running}
                size={'small'}
                type={'default'}
                style={{marginBottom: 16}}
                onClick={() => {
                    const histories = appContext?.history;
                    const results = []

                    const missing_messages = []
                    for (const history of histories) {
                        const messages = []
                        const operations = history.operations
                        for (const operation of operations) {
                            const children = operation.children
                            for (const child of children) {
                                if(child?.originalData?.result?.length > 0 && child?.originalData?.ai_result?.messages?.length === 0) {
                                    missing_messages.push(child)
                                }

                                if (child.originalData?.ai_result?.messages?.length > 0) {
                                    for (const element of child.originalData?.ai_result?.messages) {
                                        messages.push(element)
                                    }
                                }
                            }
                        }

                        const resolvedHistorySel = resolveHistorySelection(history) || {base: {}, compare: {}}
                        let report = {
                            missing_messages,
                            type: history.type,
                            base_project: {
                                project: {
                                    title: resolvedHistorySel.base?.project?.information?.title,
                                    descriptions: resolvedHistorySel.base?.project?.information?.descriptions,
                                },
                                plan: {
                                    title: resolvedHistorySel.base?.plan?.plan?.title,
                                    version: resolvedHistorySel.base?.plan?.plan?.version,
                                },
                                river: {
                                    river: history.selection.base?.river?.river || null,
                                    reach: history.selection.base?.river?.reach || null,
                                },
                                summary: {
                                    river_list: history.summary?.base?.river_list || [],
                                    length: history.summary?.base?.length || 0,
                                    crossSections: history.summary?.base?.crossSections || 0,
                                    bridges: history.summary?.base?.bridges || 0,
                                    single_bridges: history.summary?.base?.single_bridges || 0,
                                    multiple_bridges: history.summary?.base?.multiple_bridges || 0,
                                    inline: history.summary?.base?.inline || 0,
                                    culverts: history.summary?.base?.culverts || 0,
                                    profiles: history.summary?.base?.profiles || 0,
                                    flowLocations: history.summary?.base?.flowLocations || 0,
                                    calculation_tolerances: history.summary?.base?.calculation_tolerances
                                },
                            }
                        }

                        if (report.type === 'Comparison') {
                            report.compare_project = {
                                project: {
                                    title: resolvedHistorySel.compare?.project?.information?.title,
                                    descriptions: resolvedHistorySel.compare?.project?.information?.descriptions,
                                },
                                plan: {
                                    title: resolvedHistorySel.compare?.plan?.plan?.title,
                                    version: resolvedHistorySel.compare?.plan?.plan?.version,
                                },
                                river: {
                                    river: history.selection?.compare?.river?.river || null,
                                    reach: history.selection?.compare?.river?.reach || null,
                                },
                                summary: {
                                    river_list: history.summary?.compare?.river_list || [],
                                    length: history.summary?.compare?.length || 0,
                                    crossSections: history.summary?.compare?.crossSections || 0,
                                    bridges: history.summary?.compare?.bridges || 0,
                                    single_bridges: history.summary?.compare?.single_bridges || 0,
                                    multiple_bridges: history.summary?.compare?.multiple_bridges || 0,
                                    inline: history.summary?.compare?.inline || 0,
                                    culverts: history.summary?.compare?.culverts || 0,
                                    profiles: history.summary?.compare?.profiles || 0,
                                    flowLocations: history.summary?.compare?.flowLocations || 0,
                                    calculation_tolerances: history.summary?.base?.calculation_tolerances
                                },
                            }
                        }

                        report.messages = messages

                        results.push(report)
                    }


                    setTimeout(() => {
                        const filename = `ai_results_${new Date().toISOString().replaceAll(':', '').split('.')[0]}.json`
                        const jsonString = JSON.stringify(results, null, 2);
                        const blob = new Blob([jsonString], { type: "application/json" });
                        const url = URL.createObjectURL(blob);

                        const a = document.createElement("a");
                        a.href = url;
                        a.download = filename;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);

                        URL.revokeObjectURL(url);
                    }, [1000])
                }}
            >
                Download Json
            </Button>
            <HtmlReportButton style={{marginBottom: 16, marginLeft: 8}}/>
            {runType !== 'Comparison' && (
                <Button
                    disabled={running.running}
                    size={'small'}
                    type={'default'}
                    style={{marginBottom: 16, marginLeft: 8}}
                    onClick={() => setSummaryModalVisible(true)}
                >
                    Summary Table
                </Button>
            )}
            {runType === 'Check' && (
                <Button
                    disabled={running.running || !(appContext?.history || []).some(h => h?.type === 'Check')}
                    size={'small'}
                    type={'default'}
                    style={{marginBottom: 16, marginLeft: 8}}
                    onClick={() => setMessageCountsModalVisible(true)}
                >
                    Message Counts
                </Button>
            )}
            <SummaryTableModal
                visible={summaryModalVisible}
                onClose={() => setSummaryModalVisible(false)}
                histories={appContext?.history}
            />
            {runType === 'Check' && (
                <MessageCountsModal
                    visible={messageCountsModalVisible}
                    onClose={() => setMessageCountsModalVisible(false)}
                    histories={appContext?.history}
                />
            )}
            <div
                style={{
                    marginBottom: 8,
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                }}
            >
                <div style={{flex: 1}}>
                    <Popover
                        placement={'bottomLeft'}
                        trigger={'click'}
                        content={
                            <PopoverScrollContent maxHeight={600}>
                                <SelectPlan type={'base'}/>
                            </PopoverScrollContent>
                        }
                    >
                        <SelectionInfo type={'base'} />
                    </Popover>
                    <br/>
                    <ProjectInfo
                        autoHeight={true}
                        showNotes={true}
                        folderName={
                            selection?.base.project?.information?.folderName
                        }
                        title={selection?.base.project?.information?.title}
                        descriptions={
                            selection?.base.project?.information?.descriptions
                        }
                        planTitle={selection?.base.plan?.plan?.title}
                        version={
                            selection?.base.project?.projectFiles &&
                            selection.base.project?.projectFiles?.length > 0 &&
                            selection?.base.plan?.plan?.version
                        }
                    />
                </div>
                {runType === 'Comparison' && (
                    <div style={{flex: 1}}>
                        <Popover
                            placement={'bottomLeft'}
                            trigger={'click'}
                            content={
                                <PopoverScrollContent maxHeight={600}>
                                    <SelectPlan type={'compare'}/>
                                </PopoverScrollContent>
                            }
                        >
                            <SelectionInfo type={'compare'} />
                        </Popover>
                        <ProjectInfo
                            autoHeight={true}
                            showNotes={true}
                            folderName={
                                selection.compare.project?.information
                                    ?.folderName
                            }
                            title={
                                selection.compare.project?.information?.title
                            }
                            descriptions={
                                selection.compare.project?.information
                                    ?.descriptions
                            }
                            planTitle={selection.compare.plan?.plan?.title}
                            version={
                                selection.compare.project?.projectFiles &&
                                selection.compare.project?.projectFiles
                                    ?.length > 0 &&
                                selection.compare.plan?.plan?.version
                            }
                        />
                    </div>
                )}
            </div>

            {/* Projection selector - shown when any project is missing .prj file */}
            {(!selection?.base?.project?.projection?.text || 
              (runType === 'Comparison' && !selection?.compare?.project?.projection?.text)) && (
                <div style={{
                    marginBottom: 16,
                    padding: '8px 12px',
                    backgroundColor: '#fffbe6',
                    border: '1px solid #ffe58f',
                    borderRadius: 4,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: 6
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
                        <WarningOutlined style={{ color: '#faad14', fontSize: 14, flexShrink: 0 }} />
                        <div style={{ fontSize: 12, lineHeight: 1.3 }}>
                            Projection file (.prj) not found
                            {!selection?.base?.project?.projection?.text && runType === 'Check' && (
                                <span style={{ fontWeight: 'normal', color: '#666' }}> for this project</span>
                            )}
                            {runType === 'Comparison' && (
                                <span style={{ fontWeight: 'normal', color: '#666' }}>
                                    {!selection?.base?.project?.projection?.text && !selection?.compare?.project?.projection?.text 
                                        ? ' for both projects'
                                        : !selection?.base?.project?.projection?.text 
                                            ? ' for base project'
                                            : ' for compare project'}
                                </span>
                            )}
                        </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                        <div style={{ fontSize: 11, color: '#666' }}>
                            Default projection
                        </div>
                        <Select
                            size="small"
                            style={{ width: 150 }}
                            value={defaultProjectionKey}
                            onChange={setDefaultProjectionKey}
                            options={getProjectionOptions()}
                        />
                    </div>
                </div>
            )}

            <Divider/>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    marginBottom: 8,
                }}
            >
                <Button
                    disabled={running.running}
                    size={'small'}
                    style={{width: 100}}
                    onClick={() => {
                        setOperations([
                            ...getOperations(
                                selection,
                                getSelectionId(selection),
                                !isOperationSelectedAll()
                            ),
                        ]);
                    }}
                >
                    {isOperationSelectedAll() ? 'Deselect All' : 'Select All'}
                </Button>
                <Button.Group>
                    <Button
                        loading={running.running}
                        disabled={running.running}
                        size={'small'}
                        type={'primary'}
                        icon={<CaretRightOutlined/>}
                        onClick={async () => {
                            const {histories, id} = await run(
                                [...appContext?.history],
                                selection,
                                operations,
                                (ops) => setOperations([...ops])
                            );
                            
                            dispatchProject({
                                type: AppActions.SET_HISTORY,
                                data: {
                                    history: histories,
                                },
                            });

                            dispatchProject({
                                type: AppActions.SET_SELECTED_HISTORY_ID,
                                data: {
                                    id: id,
                                },
                            });
                        }}
                    />
                    {runType === 'Check' && (
                        <Button
                            loading={running.running}
                            disabled={running.running}
                            size={'small'}
                            type={'primary'}
                            style={{marginLeft: 4}}
                            icon={<ForwardOutlined/>}
                            onClick={batchRun}
                        />
                    )}
                </Button.Group>
                <Button.Group>
                    <Tooltip title={'Go To Message'}>
                        <Button
                            disabled={!findHistory() || running.running}
                            size={'small'}
                            icon={<TableOutlined/>}
                            type={'default'}
                            onClick={() => {
                                const item = getFirstErrorMessage();
                                dispatchProject({
                                    type: AppActions.PUSH_OPERATION_MESSAGE,
                                    data: {
                                        id: getSelectionId(selection),
                                        operation: item,
                                        tab: 'Message',
                                    },
                                });
                                history.push('/app/results');
                            }}
                        />
                    </Tooltip>
                    <Tooltip title={'Go To Plot'}>
                        <Button
                            disabled={!findHistory() || running.running}
                            size={'small'}
                            icon={<DotChartOutlined/>}
                            type={'default'}
                            onClick={() => {
                                const item = getFirstErrorMessage();
                                dispatchProject({
                                    type: AppActions.PUSH_OPERATION_MESSAGE,
                                    data: {
                                        id: getSelectionId(selection),
                                        operation: item,
                                        tab: 'Plot',
                                    },
                                });
                                history.push('/app/results');
                            }}
                        />
                    </Tooltip>
                    <Tooltip title={'Go To ui'}>
                        <Button
                            disabled={!findHistory() || running.running}
                            size={'small'}
                            icon={<PicLeftOutlined/>}
                            type={'default'}
                            onClick={() => {
                                const item = getFirstErrorMessage();
                                dispatchProject({
                                    type: AppActions.PUSH_OPERATION_MESSAGE,
                                    data: {
                                        id: getSelectionId(selection),
                                        operation: item,
                                        tab: 'UI',
                                    },
                                });
                                history.push('/app/results');
                            }}
                        />
                    </Tooltip>
                    <Tooltip title={'Go To Map'}>
                        <Button
                            disabled={
                                !findHistory() ||
                                running.running ||
                                !hasGeoReference(selection)
                            }
                            size={'small'}
                            icon={<GlobalOutlined/>}
                            type={'default'}
                            onClick={() => {
                                const item = getFirstErrorMessage();
                                dispatchProject({
                                    type: AppActions.PUSH_OPERATION_MESSAGE,
                                    data: {
                                        id: getSelectionId(selection),
                                        operation: item,
                                        tab: 'Map',
                                    },
                                });
                                history.push('/app/results');
                            }}
                        />
                    </Tooltip>
                    {runType === 'Check' && (
                        <Tooltip title={'Score Analysis'}>
                            <Button
                                disabled={!findHistory() || running.running}
                                size={'small'}
                                icon={<BarChartOutlined/>}
                                type={'default'}
                                onClick={() => setScoreAnalysisModalVisible(true)}
                            />
                        </Tooltip>
                    )}
                </Button.Group>
                {runType === 'Comparison' && (
                    <Checkbox
                        checked={assoc}
                        onChange={(e) => setAssoc(e.target.checked)}
                    >
                        Associate Structure
                    </Checkbox>
                )}
                <Spin spinning={running.running} size={'small'}/>
                <div style={{display: 'flex', flexDirection: 'column', lineHeight: 1.3}}>
                    <span>{running.text}</span>
                    {running.batchInfo && (
                        <div style={{fontSize: 11, color: '#666', display: 'flex', flexDirection: 'column'}}>
                            <span>
                                Plan/River {running.batchInfo.current}/{running.batchInfo.total}
                                {' '}({running.batchInfo.remaining} remaining): {running.batchInfo.planName}
                                {running.batchInfo.riverName ? ` / ${running.batchInfo.riverName}` : ''}
                            </span>
                            {running.batchInfo.planCount != null && running.batchInfo.riverCount != null && (
                                <span>
                                    Total: {running.batchInfo.planCount} plan, {running.batchInfo.riverCount} river = {running.batchInfo.total} runs
                                </span>
                            )}
                        </div>
                    )}
                </div>
            </div>
            
            {/* Score Analysis Modal - only for Check mode */}
            {runType === 'Check' && (
                <ScoreAnalysisModal
                    visible={scoreAnalysisModalVisible}
                    onClose={() => setScoreAnalysisModalVisible(false)}
                    history={findHistory()}
                />
            )}
            
            <OperationMenu
                onClick={(item) => {
                    if (
                        !appContext?.history?.find(
                            (x) => x.id === getSelectionId(selection)
                        ) ||
                        running.running
                    )
                        return;

                    dispatchProject({
                        type: AppActions.SET_SELECTED_HISTORY_ID,
                        data: {
                            id: getSelectionId(selection),
                            operation: item,
                        },
                    });
                    history.push('/app/results');
                }}
                operations={operations}
                onCheckedChanged={(items, val) => {
                    const _operations = [...operations];
                    items.forEach((item) => {
                        _operations.forEach((op) => {
                            op.children.forEach((child) => {
                                if (child.title === item.title)
                                    child.enable = val;
                            });
                        });
                    });

                    setOperations(_operations);
                }}
            />
        </div>
    );
}
